<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { makeVDialogProps, VDialog } from 'vuetify/lib/components/VDialog/VDialog.mjs'

import { dialogSizes } from './types'
import type { DialogSize } from './types'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'

import mdiFullscreen from '~icons/mdi/fullscreen'
import mdiFullscreenExit from '~icons/mdi/fullscreen-exit'

const props = defineProps({
  hideFooter: {
    type: Boolean,
    default: false,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  fullscreenAction: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
  size: {
    type: String as () => DialogSize,
    default: undefined,
    validator: (v: string) => Object.keys(dialogSizes).includes(v),
  },
  /**
   * When true, user-initiated close attempts (ESC, click-outside, footer close button)
   * show a Yes/No confirm before closing. Programmatic `v-model = false` from parent
   * bypasses this — use it for save flows and other parent-driven close.
   */
  confirmOnClose: {
    type: Boolean,
    default: false,
  },
  confirmOnCloseTitle: {
    type: String,
    default: 'common.confirm_close_dialog_title',
  },
  confirmOnCloseMessage: {
    type: String,
    default: 'common.confirm_close_dialog_message',
  },
  ...makeVDialogProps(),
})
const emit = defineEmits<{
  'after-leave': []
}>()
const model = defineModel<boolean>()

const { t } = useI18n()

const id = useId()
const isFullscreen = ref(false)
const showConfirmClose = ref(false)

const resolvedWidth = computed(() => {
  if (props.width != null) return props.width
  if (props.size != null) return dialogSizes[props.size]
  return undefined
})

// Exclude modelValue from v-dialog's filtered props — we drive it via :model-value="model" below.
const vDialogProps = computed(() => {
  const filtered = { ...VDialog.filterProps(props) } as Record<string, unknown>
  delete filtered.modelValue
  return filtered
})

// Routes user-initiated close attempts through the confirm logic.
// Programmatic `model = false` from the parent bypasses this — that's intentional.
function requestClose() {
  if (props.confirmOnClose) {
    showConfirmClose.value = true
  } else {
    model.value = false
  }
}

// Vuetify-style spreadable props object — exposed via the #footer slot prop.
// Consumers spread it on their cancel button: `<v-btn v-bind="closeProps">`.
const closeProps = { onClick: requestClose }

function onVDialogUpdate(val: boolean) {
  // v-dialog only emits update:model-value=false for ESC / click-outside.
  // Programmatic close from parent goes through the prop binding silently.
  if (!val) {
    requestClose()
  }
}

function afterLeave() {
  isFullscreen.value = false
  emit('after-leave')
}

function confirmClose() {
  showConfirmClose.value = false
  model.value = false
}

function cancelClose() {
  showConfirmClose.value = false
}
</script>

<template>
  <v-dialog
    v-bind="vDialogProps"
    :model-value="model"
    :width="resolvedWidth"
    :aria-labelledby="props.hideHeader ? undefined : id"
    :aria-label="
      props.hideHeader && props.ariaLabel != null && props.ariaLabel.trim() !== '' ? props.ariaLabel : undefined
    "
    :fullscreen="isFullscreen"
    @update:model-value="onVDialogUpdate"
    @after-leave="afterLeave"
  >
    <v-card class="d-flex flex-column">
      <template v-if="!props.hideHeader">
        <v-card-title
          :id="id"
          class="d-flex justify-space-between align-center w-100 pa-4"
        >
          <div
            class="overflow-hidden text-no-wrap"
            style="text-overflow: ellipsis"
          >
            <slot name="title" />
          </div>

          <div class="d-flex align-center ga-2">
            <slot name="header-actions" />

            <b-tooltip-btn
              v-if="fullscreenAction"
              :icon="isFullscreen ? mdiFullscreenExit : mdiFullscreen"
              :tooltip="isFullscreen ? 'exit_fullscreen' : 'fullscreen'"
              variant="text"
              density="compact"
              @click="isFullscreen = !isFullscreen"
            />
          </div>
        </v-card-title>

        <v-divider />
      </template>

      <div class="flex-grow-1 overflow-y-auto">
        <slot />
      </div>

      <template v-if="!props.hideFooter">
        <v-divider />

        <v-card-actions class="pa-3">
          <slot
            name="footer"
            :close-props="closeProps"
          >
            <v-btn
              color="primary"
              v-bind="closeProps"
            >
              {{ t('common.close') }}
            </v-btn>
          </slot>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="showConfirmClose"
    persistent
    :width="dialogSizes.small"
    :aria-labelledby="`${id}-confirm`"
  >
    <v-card>
      <v-card-title
        :id="`${id}-confirm`"
        class="pa-4"
      >
        {{ t(props.confirmOnCloseTitle) }}
      </v-card-title>

      <v-divider />

      <div>
        <v-card-text>
          {{ t(props.confirmOnCloseMessage) }}
        </v-card-text>
      </div>

      <v-divider />

      <v-card-actions class="pa-3">
        <v-spacer />

        <v-btn
          variant="text"
          color="primary"
          @click="cancelClose"
        >
          {{ t('common.cancel') }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          @click="confirmClose"
        >
          {{ t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
